<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WabaCurrencyMigrationsAPI from 'custom/dashboard/api/wabaCurrencyMigrations';
import { migrationApiError } from 'custom/dashboard/helper/zimobi/wabaCurrencyMigrationHelper';
import MigrationStatusBadge from './MigrationStatusBadge.vue';
import MigrationChecklist from './MigrationChecklist.vue';
import MigrationStep from './MigrationStep.vue';
import MigrationTimeline from './MigrationTimeline.vue';

const PREFIX = 'CLIENT_SETTINGS.CURRENCY_MIGRATION';
// Statuses that only a Meta read can move on. While the migration sits in one of them the
// screen re-reads our own record on a timer — the polling job is what talks to Meta, so
// following it costs nothing there.
const POLLABLE = ['cloning', 'completing'];
const REFRESH_INTERVAL = 15000;
const META_FAILURES = ['FAILED', 'REJECTED'];

const { t } = useI18n();
const route = useRoute();

const migrationId = route.params.migrationId;

const isFetching = ref(true);
// One flag for every action, so a running step both shows its own spinner and locks the
// others: initiating and completing must never overlap.
const busy = ref('');
const migration = ref({});
const events = ref([]);
const checklistItems = ref({});
const confirmDialogRef = ref(null);

let pollTimer = null;

const listRoute = computed(() => ({
  name: 'client_settings_currency_migrations',
  params: { accountId: route.params.accountId },
}));

const timestamp = value => (value ? new Date(value).toLocaleString() : '');

const summary = computed(() => {
  const record = migration.value;

  return [
    ['INBOX', record.inbox_name],
    ['SOURCE_WABA', record.source_waba_id],
    ['SOURCE_CURRENCY', record.source_currency],
    ['TARGET_CURRENCY', record.target_currency],
    ['DESTINATION_WABA', record.destination_waba_id],
    ['EXTENDED_CREDIT_ID', record.extended_credit_id],
    ['MIGRATION_ID', record.migration_id],
    ['META_STATUS', record.meta_status],
    ['CREATED_BY', record.created_by],
    ['INITIATED_AT', timestamp(record.initiated_at)],
    ['COMPLETED_AT', timestamp(record.completed_at)],
    ['LAST_POLLED_AT', timestamp(record.last_polled_at)],
  ].map(([key, value]) => ({
    key,
    value: value || t(`${PREFIX}.FIELDS.EMPTY`),
  }));
});

// Which step the migration is on. A failure sits on the cloning step rather than on a step
// of its own, so the guidance the admin needs stays next to it — the only failure that can
// happen past the clone is the retryable one, and that is `action_required`, not `failed`.
const currentStep = computed(() => {
  switch (migration.value.status) {
    case 'cloning':
    case 'failed':
      return 1;
    case 'ready_to_complete':
    case 'action_required':
    case 'completing':
      return 2;
    case 'completed':
      return 3;
    default:
      return 0;
  }
});

const isFailed = computed(() => migration.value.status === 'failed');

// Meta answered the phone number move with FAILED_TO_COMPLETE. Not a failure: the clone is
// there and the move is retryable as soon as the destination WABA has a payment method, so
// the screen asks for that instead of reporting a dead migration.
const needsManualAction = computed(
  () => migration.value.status === 'action_required'
);

const stepState = index => {
  if (index < currentStep.value) return 'done';
  if (index === currentStep.value) return 'active';
  return 'pending';
};

const isConfirmed = step =>
  (checklistItems.value[step] ?? []).every(
    item => migration.value.checklist?.[item]
  );

// Meta's own payments screen, pointed straight at the WABA the numbers are moving to.
// `business_id` is left out on purpose: it is per portfolio and we never learn it, so Meta
// resolves it from the session instead of us guessing it.
const paymentSettingsUrl = computed(() => {
  const waba = migration.value.destination_waba_id;
  if (!waba) return '';

  return `https://business.facebook.com/latest/settings/whatsapp_account?selected_asset_id=${waba}&selected_asset_type=whatsapp-business-account`;
});

const checklistLinks = computed(() =>
  paymentSettingsUrl.value
    ? {
        payment_method_attached: {
          url: paymentSettingsUrl.value,
          label: t(`${PREFIX}.ACTIONS.OPEN_PAYMENT_SETTINGS`),
        },
      }
    : {}
);

const troubleshooting = computed(() => {
  const status = migration.value.meta_status;
  return META_FAILURES.includes(status)
    ? t(`${PREFIX}.TROUBLESHOOTING.${status}`)
    : '';
});

const applyResponse = data => {
  migration.value = data;
  events.value = data.events ?? [];
  checklistItems.value = data.checklist_items ?? {};
  syncPolling();
};

const fetchMigration = async ({ silent = true } = {}) => {
  if (!silent) isFetching.value = true;
  try {
    const { data } = await WabaCurrencyMigrationsAPI.show(migrationId);
    applyResponse(data);
  } catch (error) {
    // A failed background tick is left alone: the next one retries, and alerting on every
    // one of them would bury the screen.
    if (!silent) useAlert(t(`${PREFIX}.API.FETCH_ERROR`));
  } finally {
    if (!silent) isFetching.value = false;
  }
};

const stopPolling = () => {
  clearInterval(pollTimer);
  pollTimer = null;
};

function syncPolling() {
  if (!POLLABLE.includes(migration.value.status)) {
    stopPolling();
    return;
  }
  pollTimer ||= setInterval(fetchMigration, REFRESH_INTERVAL);
}

const run = async (action, request, successKey) => {
  busy.value = action;
  try {
    const { data } = await request();
    applyResponse(data);
    if (successKey) useAlert(t(successKey));
  } catch (error) {
    useAlert(migrationApiError(error, t(`${PREFIX}.API.ACTION_ERROR`)));
  } finally {
    busy.value = '';
  }
};

const onConfirmChecklist = confirmations =>
  run(
    'checklist',
    () => WabaCurrencyMigrationsAPI.confirmChecklist(migrationId, confirmations),
    null
  );

const onRefresh = () =>
  run(
    'refresh',
    () => WabaCurrencyMigrationsAPI.refresh(migrationId),
    null
  );

const onInitiate = () =>
  run(
    'initiate',
    () => WabaCurrencyMigrationsAPI.initiate(migrationId),
    `${PREFIX}.API.INITIATE_SUCCESS`
  );

const onComplete = () => {
  confirmDialogRef.value?.close();
  return run(
    'complete',
    () => WabaCurrencyMigrationsAPI.complete(migrationId),
    `${PREFIX}.API.COMPLETE_SUCCESS`
  );
};

onMounted(() => fetchMigration({ silent: false }));
onUnmounted(stopPolling);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t(`${PREFIX}.LOADING`)"
  >
    <template #header>
      <div class="flex flex-col items-start w-full gap-2">
        <BackButton
          compact
          :back-url="listRoute"
          :button-label="$t(`${PREFIX}.BACK`)"
        />
        <BaseSettingsHeader
          :title="migration.inbox_name || $t(`${PREFIX}.HEADER`)"
          :description="$t(`${PREFIX}.DESCRIPTION`)"
        >
          <template #actions>
            <!-- self-center: BaseSettingsHeader's actions slot is a plain flex row, so the
                 badge would otherwise stretch to the button's height with its text at the top. -->
            <MigrationStatusBadge
              v-if="migration.status"
              :status="migration.status"
              class="self-center"
            />
            <NextButton
              variant="faded"
              color="slate"
              icon="i-lucide-refresh-cw"
              :label="$t(`${PREFIX}.ACTIONS.REFRESH`)"
              :is-loading="busy === 'refresh'"
              :disabled="Boolean(busy)"
              data-testid="refresh-migration"
              @click="onRefresh"
            />
          </template>
        </BaseSettingsHeader>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-8">
        <dl
          class="grid grid-cols-2 gap-4 lg:grid-cols-4"
          data-testid="migration-summary"
        >
          <div v-for="field in summary" :key="field.key" class="flex flex-col gap-1">
            <dt class="text-sm text-n-slate-11">
              {{ $t(`${PREFIX}.FIELDS.${field.key}`) }}
            </dt>
            <dd
              class="text-sm font-medium break-words text-n-slate-12"
              :data-testid="`summary-${field.key.toLowerCase()}`"
            >
              {{ field.value }}
            </dd>
          </div>
        </dl>

        <div
          v-if="troubleshooting"
          class="flex flex-col gap-1 p-4 rounded-xl bg-n-ruby-3"
          data-testid="migration-troubleshooting"
        >
          <span class="text-sm font-medium text-n-ruby-11">
            {{ $t(`${PREFIX}.TROUBLESHOOTING.TITLE`) }} ·
            {{ migration.meta_status }}
          </span>
          <p class="mb-0 text-sm text-n-slate-12">{{ troubleshooting }}</p>
        </div>

        <div
          v-if="needsManualAction"
          class="flex flex-col items-start gap-1 p-4 rounded-xl bg-n-amber-3"
          data-testid="migration-action-required"
        >
          <span class="text-sm font-medium text-n-amber-11">
            {{ $t(`${PREFIX}.ACTION_REQUIRED.TITLE`) }}
          </span>
          <p class="mb-0 text-sm text-n-slate-12">
            {{ $t(`${PREFIX}.ACTION_REQUIRED.DESCRIPTION`) }}
          </p>
          <a
            v-if="paymentSettingsUrl"
            :href="paymentSettingsUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-1 text-sm font-medium text-n-blue-text hover:underline"
            data-testid="action-required-link"
          >
            {{ $t(`${PREFIX}.ACTIONS.OPEN_PAYMENT_SETTINGS`) }}
          </a>
        </div>

        <div class="flex flex-col gap-3">
          <MigrationStep
            :index="1"
            :title="$t(`${PREFIX}.STEPS.PREFLIGHT.TITLE`)"
            :state="stepState(0)"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                $t(`${PREFIX}.STEPS.PREFLIGHT.SUMMARY`, {
                  currency: migration.target_currency,
                })
              }}
            </p>
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t(`${PREFIX}.STEPS.PREFLIGHT.CHECKLIST_TITLE`) }}
            </span>
            <MigrationChecklist
              step="preflight"
              :items="checklistItems.preflight"
              :checklist="migration.checklist"
              :disabled="Boolean(busy)"
              @confirm="onConfirmChecklist"
            />
            <!-- `status`, not `initiated_at`: an initiate Meta rejected leaves the record a
                 draft and has to stay retryable, while one Meta accepted is never offered twice. -->
            <NextButton
              v-if="migration.status === 'draft'"
              class="w-fit"
              :label="$t(`${PREFIX}.STEPS.PREFLIGHT.ACTION`)"
              :is-loading="busy === 'initiate'"
              :disabled="!isConfirmed('preflight') || Boolean(busy)"
              data-testid="initiate-migration"
              @click="onInitiate"
            />
          </MigrationStep>

          <MigrationStep
            :index="2"
            :title="$t(`${PREFIX}.STEPS.CLONING.TITLE`)"
            :state="stepState(1)"
            :failed="isFailed"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t(`${PREFIX}.STEPS.CLONING.SUMMARY`) }}
            </p>
            <p
              v-if="migration.status === 'cloning'"
              class="mb-0 text-sm text-n-slate-11"
            >
              {{ $t(`${PREFIX}.STEPS.CLONING.WAITING`) }}
            </p>
          </MigrationStep>

          <MigrationStep
            :index="3"
            :title="$t(`${PREFIX}.STEPS.PRE_COMPLETION.TITLE`)"
            :state="stepState(2)"
            :failed="isFailed"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t(`${PREFIX}.STEPS.PRE_COMPLETION.SUMMARY`) }}
            </p>
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t(`${PREFIX}.STEPS.PRE_COMPLETION.CHECKLIST_TITLE`) }}
            </span>
            <MigrationChecklist
              step="pre_completion"
              :items="checklistItems.pre_completion"
              :checklist="migration.checklist"
              :links="checklistLinks"
              :disabled="Boolean(busy) || !migration.completable"
              @confirm="onConfirmChecklist"
            />
            <NextButton
              v-if="migration.completable"
              color="ruby"
              class="w-fit"
              :label="
                needsManualAction
                  ? $t(`${PREFIX}.STEPS.PRE_COMPLETION.RETRY_ACTION`)
                  : $t(`${PREFIX}.STEPS.PRE_COMPLETION.ACTION`)
              "
              :is-loading="busy === 'complete'"
              :disabled="!isConfirmed('pre_completion') || Boolean(busy)"
              data-testid="complete-migration"
              @click="confirmDialogRef?.open()"
            />
          </MigrationStep>

          <MigrationStep
            :index="4"
            :title="$t(`${PREFIX}.STEPS.POST_MIGRATION.TITLE`)"
            :state="stepState(3)"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                $t(`${PREFIX}.STEPS.POST_MIGRATION.SUMMARY`, {
                  currency: migration.target_currency,
                })
              }}
            </p>
            <p
              v-if="migration.adopted_at"
              class="mb-0 text-sm text-n-teal-11"
              data-testid="inbox-repointed"
            >
              {{
                $t(`${PREFIX}.STEPS.POST_MIGRATION.INBOX_REPOINTED`, {
                  waba: migration.destination_waba_id,
                })
              }}
            </p>
            <p
              v-else
              class="mb-0 text-sm text-n-amber-11"
              data-testid="inbox-not-repointed"
            >
              {{
                $t(`${PREFIX}.STEPS.POST_MIGRATION.INBOX_NOT_REPOINTED`, {
                  waba: migration.destination_waba_id,
                })
              }}
            </p>
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t(`${PREFIX}.STEPS.POST_MIGRATION.CHECKLIST_TITLE`) }}
            </span>
            <MigrationChecklist
              step="post_migration"
              :items="checklistItems.post_migration"
              :checklist="migration.checklist"
              :disabled="Boolean(busy)"
              @confirm="onConfirmChecklist"
            />
          </MigrationStep>
        </div>

        <MigrationTimeline :events="events" />
      </div>

      <Dialog
        ref="confirmDialogRef"
        type="alert"
        :title="$t(`${PREFIX}.STEPS.PRE_COMPLETION.CONFIRM_TITLE`)"
        :description="
          $t(`${PREFIX}.STEPS.PRE_COMPLETION.CONFIRM_DESCRIPTION`, {
            inbox: migration.inbox_name,
            waba: migration.destination_waba_id,
          })
        "
        :confirm-button-label="
          $t(`${PREFIX}.STEPS.PRE_COMPLETION.CONFIRM_ACTION`)
        "
        :is-loading="busy === 'complete'"
        @confirm="onComplete"
      />
    </template>
  </SettingsLayout>
</template>
